import { useState } from 'react';
import { Eye, EyeOff, Loader2, Mail } from 'lucide-react';
import { authClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

/** Classify signInWithPassword errors: prefer HTTP status, fall back to message text */
function classifyCredentialError(err: unknown): string {
  const status = err != null && typeof err === 'object' && 'status' in err
    ? (err as { status: unknown }).status
    : undefined;
  if (status === 429) return 'Too many attempts — please wait a moment and try again';
  if (status === 400 || status === 401) return 'Password is incorrect';
  const msg = (err instanceof Error ? err.message : '').toLowerCase();
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('timeout')) {
    return 'Network error — check your connection and try again';
  }
  return 'Something went wrong — please try again';
}

export function ChangeEmailForm() {
  const { user } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const currentEmail = user?.email ?? '';

  const resetForm = () => {
    setNewEmail('');
    setPassword('');
    setError(null);
    setShowPassword(false);
    setIsSubmitted(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Pre-validation: trim + lowercase
    const trimmedEmail = newEmail.trim().toLowerCase();

    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (trimmedEmail === currentEmail.toLowerCase()) {
      setError('New email must be different from your current email');
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Verify current password
      const { error: signInError } = await authClient.auth.signInWithPassword({
        email: currentEmail,
        password,
      });

      if (signInError) {
        setError(classifyCredentialError(signInError));
        setIsSubmitting(false);
        return;
      }

      // Step 2: Request email change (Supabase handles verification emails)
      const { error: updateError } = await authClient.auth.updateUser(
        { email: trimmedEmail },
        { emailRedirectTo: `${window.location.origin}/auth/callback?type=email_change` }
      );

      if (updateError) {
        setError(updateError.message || 'Failed to request email change');
        setIsSubmitting(false);
        return;
      }

      // Step 3: Store pending email in sessionStorage for AuthCallback comparison
      try {
        sessionStorage.setItem('filmple_pending_email', trimmedEmail);
      } catch {
        // sessionStorage unavailable (private browsing, etc.) — non-fatal
      }

      // Success — show confirmation message
      setIsSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Post-submission state
  if (isSubmitted) {
    return (
      <div className="pt-4 border-t border-white/[0.06]">
        <div className="text-white font-medium mb-2">Change Email</div>
        <div className="p-4 bg-teal-500/10 rounded-lg">
          <p className="text-sm text-teal-400">
            Confirmation emails sent to both your current and new address.
            Verify both to complete the change.
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setIsOpen(false); }}
          className="mt-3 text-sm text-slate-400 hover:text-white transition-colors"
        >
          Done
        </button>
      </div>
    );
  }

  // Collapsed state
  if (!isOpen) {
    return (
      <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
        <div>
          <div className="text-white font-medium">Change Email</div>
          <div className="text-sm text-slate-400 flex items-center gap-1.5">
            <Mail size={14} />
            {currentEmail}
          </div>
        </div>
        <button
          onClick={() => setIsOpen(true)}
          className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium"
        >
          Change
        </button>
      </div>
    );
  }

  // Expanded form
  return (
    <div className="pt-4 border-t border-white/[0.06]">
      <div className="text-white font-medium mb-1">Change Email</div>
      <div className="text-sm text-slate-400 mb-3">
        Current: {currentEmail}
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* New Email */}
        <div>
          <label htmlFor="newEmail" className="block text-sm text-slate-400 mb-1">
            New Email Address
          </label>
          <input
            id="newEmail"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Enter new email"
            className="w-full px-4 py-2.5 bg-[#111111] border border-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
            required
            autoFocus
          />
        </div>

        {/* Password Verification */}
        <div>
          <label htmlFor="emailChangePassword" className="block text-sm text-slate-400 mb-1">
            Current Password
          </label>
          <div className="relative">
            <input
              id="emailChangePassword"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Verify your identity"
              className="w-full px-4 py-2.5 bg-[#111111] border border-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => { resetForm(); setIsOpen(false); }}
            disabled={isSubmitting}
            className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Sending...
              </>
            ) : (
              'Send Verification'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
